import _ from 'lodash';
import {useCallback, useMemo, useState} from 'react';

export function useDebouncedValue<T>(
  initialValue: T,
  delay: number,
  onChange?: (newValue: T) => void // called when the debounced value changes.
  // NOTE: This should be a memoized function using `useCallback`.
  // Or else the debounced function will be recreated on every render and the debounce will not work.
  // There isn't an easy way to type this to enforce it.
): {
  value: T;
  debouncedSetValue: (newValue: T) => void;
  instantSetValue: (newValue: T) => void;
} {
  const [value, setValue] = useState<T>(initialValue);
  const instantSetValue = useCallback(
    (newValue: T) => {
      setValue(newValue);
      onChange?.(newValue);
    },
    [onChange]
  );

  const debouncedSetValue = useMemo(
    () => _.debounce(instantSetValue, delay),
    [delay, instantSetValue]
  );

  return useMemo(
    () => ({value, debouncedSetValue, instantSetValue}),
    [value, debouncedSetValue, instantSetValue]
  );
}
